import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [countries, setCountries] = useState([]);
  const [geo, setGeo] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
    // Try to preload geo for city hints. If unauthenticated, the /compass/geo
    // endpoint may 401 — that's fine, we fall back to free-text.
    api.get("/compass/geo").then((r) => setGeo(r.data.geo || {})).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const cityOptions = country && geo[country]
    ? geo[country].flatMap((s) => s.cities)
    : [];

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    if (password !== confirm) return toast.error("Las contraseñas no coinciden");
    if (!country) return toast.error("Selecciona tu país");
    if (!city.trim()) return toast.error("Ingresa tu ciudad");
    setLoading(true);
    try {
      await register(email, password, name, {
        last_name: lastName.trim() || null,
        country,
        city: city.trim(),
      });
      toast.success("Cuenta creada. ¡Empecemos!");
      navigate("/brujula");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la cuenta");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-foreground">
      <div className="hidden md:flex flex-col justify-between p-12 border-r-2 border-foreground bg-foreground text-background">
        <div className="display text-6xl leading-none">RUTA</div>
        <div>
          <div className="display text-4xl leading-tight max-w-md">
            Menos dudas.<br />Más dirección.
          </div>
          <p className="mt-4 max-w-md text-sm opacity-80 leading-relaxed">
            Encuentra claridad, evalúa tus opciones y construye con confianza.
          </p>
        </div>
        <div className="label-mono opacity-70">Latinoamérica · Español</div>
      </div>

      <div className="flex items-start justify-center p-6 md:p-12 py-10">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div>
            <div className="label-mono text-muted-foreground">Crear cuenta</div>
            <h1 className="display text-4xl md:text-5xl mt-2">Empecemos</h1>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="label-mono block mb-2">Nombre</div>
              <input required className="input-brut" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" />
            </label>
            <label className="block">
              <div className="label-mono block mb-2">Apellido <span className="opacity-60">(opcional)</span></div>
              <input className="input-brut" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="register-lastname" />
            </label>
          </div>

          <label className="block">
            <div className="label-mono block mb-2">Correo</div>
            <input type="email" required className="input-brut" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="label-mono block mb-2">Contraseña</div>
              <input type="password" required className="input-brut" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
            </label>
            <label className="block">
              <div className="label-mono block mb-2">Confirmar</div>
              <input type="password" required className="input-brut" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="register-confirm" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="label-mono block mb-2">País</div>
              <select
                required
                className="input-brut"
                value={country}
                onChange={(e) => { setCountry(e.target.value); setCity(""); }}
                data-testid="register-country"
              >
                <option value="">Elige…</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="label-mono block mb-2">Ciudad</div>
              {cityOptions.length > 0 ? (
                <select required className="input-brut" value={city} onChange={(e) => setCity(e.target.value)} data-testid="register-city">
                  <option value="">Elige…</option>
                  {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input
                  required
                  className="input-brut"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder={country ? "Escribe tu ciudad" : "Elige país primero"}
                  disabled={!country}
                  data-testid="register-city"
                />
              )}
            </label>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="btn-register-submit">
            {loading ? "Creando…" : "Crear cuenta"}
            <ArrowRight size={16} weight="bold" />
          </button>

          <div className="text-sm">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="font-semibold underline decoration-2 underline-offset-4" data-testid="link-login">
              Inicia sesión
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
